// Student view — full-screen, no brand. Mobile-first: tap = click,
// drag = scroll, soft keyboard pops up on tap (via hidden <input>),
// IME (composition) works on iOS/Android.

import { useEffect, useRef, useState } from 'react';
import {
  newCallsClient,
  type CallsClient,
  type RemoteTrackEntry,
} from '../lib/calls';
import { captureInput, type InputCaptureHandle, type InputEvent } from '../lib/input';

type Phase = 'connecting' | 'awaiting-operator' | 'connected' | 'error' | 'expired';

interface ConnectResponse {
  ok: true; wsUrl: string; operatorId: string; expiresAt: number; callsAppId: string | null;
}
interface ErrorResponse { error: string; }


function collectDeviceInfo() {
  const w = window;
  // Use inner{Width,Height} — that's the ACTUAL visible viewport the
  // student sees (excludes Safari URL bar which dynamically hides on
  // scroll). The resize listener will refresh on toolbar show/hide so the
  // operator emulation tracks the live visible area.
  return {
    userAgent: navigator.userAgent,
    width:  Math.round(w.innerWidth  || w.screen?.width  || 390),
    height: Math.round(w.innerHeight || w.screen?.height || 844),
    dpr: w.devicePixelRatio || 1,
    touch: 'ontouchstart' in w || (navigator.maxTouchPoints || 0) > 0,
    platform: (navigator as any).userAgentData?.platform || navigator.platform || '',
    mobile: !!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches),
    locale: navigator.language || 'en-US',
    timezone: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'UTC'; } })(),
  };
}

export default function Student({ token }: { token: string }) {
  const [phase, setPhase] = useState<Phase>('connecting');
  const [errMsg, setErrMsg] = useState<string>('');
  const [hideOverlay, setHideOverlay] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const keyboardInputRef = useRef<HTMLInputElement | null>(null);
  const callsRef = useRef<CallsClient | null>(null);
  const liveTracksRef = useRef<Map<number, MediaStream>>(new Map());
  const lastEntryRef = useRef<Map<number, RemoteTrackEntry>>(new Map());
  const liveTileIdRef = useRef<number | null>(null);
  const inputHandleRef = useRef<InputCaptureHandle | null>(null);
  const blurTimerRef = useRef<number | null>(null);
  const phaseRef = useRef<Phase>('connecting');
  // Keep the ref synced so WS close handler can read the latest phase.
  phaseRef.current = phase;

  const markConnected = () => {
    setPhase((p) => (p === 'connected' ? p : 'connected'));
    setTimeout(() => setHideOverlay(true), 600);
  };

  const sendInput = (ev: InputEvent) => {
    const ws = wsRef.current;
    if (!ws) { console.warn('[student] sendInput: no ws'); return; }
    if (ws.readyState !== WebSocket.OPEN) { console.warn('[student] sendInput: ws not open, state=' + ws.readyState); return; }
    if (liveTileIdRef.current == null) { console.warn('[student] sendInput: no tileId yet, ev=', ev.kind); }
    try {
      ws.send(JSON.stringify({ type: 'input', tileId: liveTileIdRef.current, event: ev }));
      // Compact log to avoid spamming on pointermove
      if (ev.kind !== 'mouse' || ev.type !== 'move') {
        console.log('[student] sendInput', ev.kind, (ev as any).type || (ev as any).phase || '');
      }
    } catch (e) {
      console.warn('[student] sendInput err', e);
    }
  };

  useEffect(() => {
    let ws: WebSocket | null = null;
    let cancelled = false;
    let reconnectTimer: number | undefined;

    async function attachTrack(entry: RemoteTrackEntry) {
      const calls = callsRef.current;
      if (!calls) {
        console.warn('[student] attachTrack: callsRef.current null');
        return;
      }
      // Republish detection: if we already have a stream for this tileId,
      // compare callsSessionId + trackName list. Same -> dedup. Different ->
      // operator republished; teardown old PC and subscribe fresh.
      const prevEntry = lastEntryRef.current.get(entry.tileId);
      const sameSession = prevEntry
        && prevEntry.callsSessionId === entry.callsSessionId
        && prevEntry.tracks.length === entry.tracks.length
        && prevEntry.tracks.every((t, i) => t.trackName === entry.tracks[i]?.trackName);
      if (liveTracksRef.current.has(entry.tileId) && sameSession) {
        console.log('[student] attachTrack: tileId=' + entry.tileId + ' identical entry, skip');
        return;
      }
      if (liveTracksRef.current.has(entry.tileId)) {
        console.log('[student] attachTrack: tileId=' + entry.tileId + ' republished, teardown old');
        const old = liveTracksRef.current.get(entry.tileId);
        if (old) old.getTracks().forEach((t) => t.stop());
        liveTracksRef.current.delete(entry.tileId);
      }
      lastEntryRef.current.set(entry.tileId, entry);
      console.log('[student] subscribe START', entry);
      try {
        const stream = await calls.subscribe(entry, {
          onLost: () => {
            if (cancelled) return;
            console.warn('[student] subscriber PC lost tileId=' + entry.tileId + ', resubscribing');
            const old = liveTracksRef.current.get(entry.tileId);
            if (old) old.getTracks().forEach((t) => t.stop());
            liveTracksRef.current.delete(entry.tileId);
            window.setTimeout(() => {
              if (cancelled) return;
              const latest = lastEntryRef.current.get(entry.tileId);
              if (latest) attachTrack(latest);
            }, 1500);
          },
        });
        console.log('[student] subscribe OK tracks=' + stream.getTracks().map(t => t.kind).join(','));
        liveTracksRef.current.set(entry.tileId, stream);
        liveTileIdRef.current = entry.tileId;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch((e) => console.warn('video.play()', e));
        }
        markConnected();
        wireInputCapture();
      } catch (e) {
        console.error('[student] subscribe FAILED', e);
      }
    }

    function detachTrack(tileId: number) {
      const stream = liveTracksRef.current.get(tileId);
      if (!stream) return;
      stream.getTracks().forEach((t) => t.stop());
      liveTracksRef.current.delete(tileId);
      lastEntryRef.current.delete(tileId);
      const remaining = liveTracksRef.current.entries().next().value as [number, MediaStream] | undefined;
      if (remaining) {
        liveTileIdRef.current = remaining[0];
        if (videoRef.current) videoRef.current.srcObject = remaining[1];
      } else {
        liveTileIdRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        if (inputHandleRef.current) { inputHandleRef.current.destroy(); inputHandleRef.current = null; }
        setPhase('awaiting-operator');
        setHideOverlay(false);
      }
    }

    function broadcastDeviceUpdate() {
      const w = wsRef.current;
      if (!w || w.readyState !== WebSocket.OPEN) return;
      try {
        w.send(JSON.stringify({ type: 'device-update', device: collectDeviceInfo() }));
      } catch (_) {}
    }

    function wireInputCapture() {
      if (inputHandleRef.current) return;
      const input = keyboardInputRef.current;
      const video = videoRef.current;
      if (!input || !video) return;
      // The hidden <input> IS the capture layer. iOS native-focuses it on
      // tap so the soft keyboard reliably summons. We attach pointer events
      // here directly — no separate div in between.
      const unmute = () => {
        setTimeout(() => {
          try { video.muted = false; video.play().catch(() => {}); } catch {}
        }, 1000);
        input.removeEventListener('pointerdown', unmute);
      };
      input.addEventListener('pointerdown', unmute, { once: true });
      inputHandleRef.current = captureInput(input, video, sendInput, {
        keyboardTarget: input,
      });

      // Forward device geometry on REAL rotation only. Don't listen to
      // 'resize' — iOS Safari shrinks innerHeight when the soft keyboard
      // opens, which would fire resize -> broadcast device-update ->
      // operator restarts screencast at new dims -> student <video> resizes
      // -> layout shift dismisses the keyboard.
      let resizeTimer: number | null = null;
      const onGeomChange = () => {
        if (resizeTimer) window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => { broadcastDeviceUpdate(); resizeTimer = null; }, 250);
      };
      window.addEventListener('orientationchange', onGeomChange);
      // also report once now in case anything has changed since handshake
      broadcastDeviceUpdate();

      // Cleanup hook attached to inputHandleRef destroy via wrap.
      const inner = inputHandleRef.current;
      inputHandleRef.current = {
        destroy() {
          if (resizeTimer) window.clearTimeout(resizeTimer);
          window.removeEventListener('orientationchange', onGeomChange);
          inner && inner.destroy();
        },
      };
    }

    let cachedWsUrl: string | null = null;

    async function redeemTokenOnce(): Promise<string | null> {
      if (cachedWsUrl) return cachedWsUrl;
      const res = await fetch('/api/student/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as ErrorResponse;
        if (j.error === 'expired' || j.error === 'revoked' || j.error === 'exhausted') {
          setPhase('expired'); setErrMsg(j.error);
        } else { setPhase('error'); setErrMsg(j.error || 'connect failed'); }
        return null;
      }
      const j = (await res.json()) as ConnectResponse;
      callsRef.current = newCallsClient({
        appId: j.callsAppId,
        apiBase: '/api/calls',
        studentToken: token,
      });
      cachedWsUrl = j.wsUrl;
      return j.wsUrl;
    }

    async function connect() {
      try {
        const wsUrl = await redeemTokenOnce();
        if (!wsUrl || cancelled) return;

        const dev = collectDeviceInfo();
        const dParam = 'd=' + encodeURIComponent(JSON.stringify(dev));
        const fullWsUrl = wsUrl + (wsUrl.includes('?') ? '&' : '?') + dParam;
        ws = new WebSocket(fullWsUrl);
        wsRef.current = ws;
        ws.addEventListener('open', () => {
          console.log('[student-ws] open');
          setPhase('awaiting-operator');
        });
        ws.addEventListener('message', (ev) => {
          let msg: any;
          try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); } catch (e) { console.warn('[student-ws] bad msg', e); return; }
          if (!msg || typeof msg !== 'object') return;
          console.log('[student-ws] msg', msg.type, msg);
          if (msg.type === 'tracks-snapshot' && Array.isArray(msg.tracks)) {
            console.log('[student] tracks-snapshot count=' + msg.tracks.length);
            for (const t of msg.tracks as RemoteTrackEntry[]) attachTrack(t);
            return;
          }
          if (msg.type === 'track-registered' && msg.track) {
            console.log('[student] track-registered tileId=' + msg.track.tileId);
            attachTrack(msg.track as RemoteTrackEntry);
            return;
          }
          if (msg.type === 'track-unregistered' && typeof msg.tileId === 'number') { detachTrack(msg.tileId); return; }
          if (msg.type === 'from-operator' && msg.body && typeof msg.body === 'object') {
            const body = msg.body as { type?: string; editable?: boolean; hasEditable?: boolean };
            if (body.type === 'focus-changed') {
              console.log('[student] focus-changed editable=' + body.editable);
              // Informational only — we no longer flip readOnly on focus
              // events. Page-level hasEditable is the source of truth.
              return;
            }
            if (body.type === 'page-editable') {
              console.log('[student] page-editable=' + body.hasEditable);
              const k = keyboardInputRef.current;
              if (!k) return;
              // disabled (not just readOnly): iOS shows AutoFill suggestions
              // even on readonly inputs, but won't show anything for
              // disabled. pointer events still fire so mouse forwarding is
              // unaffected.
              k.disabled = !body.hasEditable;
              if (!body.hasEditable) {
                try { k.blur(); } catch (_) {}
              }
              return;
            }
          }
        });
        ws.addEventListener('close', (ev) => {
          console.warn('[student-ws] close', { code: (ev as CloseEvent).code, reason: (ev as CloseEvent).reason });
          if (cancelled) return;
          if (phaseRef.current !== 'expired' && phaseRef.current !== 'connected') {
            setPhase('connecting');
            setHideOverlay(false);
            reconnectTimer = window.setTimeout(connect, 1500);
          } else if (phaseRef.current === 'connected') {
            // We were live — try a bounded reconnect without flipping back to
            // the ugly 'connecting' overlay.
            reconnectTimer = window.setTimeout(connect, 1500);
          }
        });
        ws.addEventListener('error', (e) => {
          console.warn('[student-ws] error', e);
          if (!cancelled) setErrMsg('connection error');
        });
      } catch (e) { setPhase('error'); setErrMsg(String((e as Error).message || e)); }
    }

    connect();

    return () => {
      cancelled = true;
      if (ws) try { ws.close(); } catch {}
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      const calls = callsRef.current;
      if (calls) calls.destroy();
      callsRef.current = null;
      liveTracksRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
      liveTracksRef.current.clear();
      lastEntryRef.current.clear();
      if (inputHandleRef.current) { inputHandleRef.current.destroy(); inputHandleRef.current = null; }
      if (blurTimerRef.current) { window.clearTimeout(blurTimerRef.current); blurTimerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  let overlayText = '';
  if (phase === 'connecting') overlayText = '連線中…';
  else if (phase === 'awaiting-operator') overlayText = '等待操作端…';
  else if (phase === 'expired') overlayText = '連結已過期或被撤回';
  else if (phase === 'error') overlayText = errMsg ? `連線失敗: ${errMsg}` : '連線失敗';

  const overlayVisible = !hideOverlay && phase !== 'connected';
  const fatal = phase === 'expired' || phase === 'error';

  return (
    <div className="student-root">
      <video ref={videoRef} className="student-video" autoPlay playsInline muted />
      {/*
        Hidden input — focused programmatically on tap. Its only job is to
        summon the OS soft keyboard and let composition events fire. Its
        value is cleared on every input.
      */}
      <input
        ref={keyboardInputRef}
        className="student-keyboard"
        type="text"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        disabled  /* default until Mac signals the remote page has editable */
        aria-label="remote control surface"
      />
      {overlayVisible && (
        <div className={`student-overlay ${fatal ? 'fatal' : ''}`}>
          <div className="overlay-card">
            <div className="dot" />
            <div className="msg">{overlayText}</div>
          </div>
        </div>
      )}
    </div>
  );
}
